import { IconButton, Tooltip } from '@chakra-ui/react';
import { ReactComponent as BoldIcon } from 'assets/icons/bold.svg';
import { ReactComponent as ItalicIcon } from 'assets/icons/italic.svg';
import { ReactComponent as UnderlineIcon } from 'assets/icons/underline.svg';
import { FONT_FAMILY_CONFIG } from 'constants/fonts';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontFamily } from 'types/font.types';
import { preventDefault } from 'utils/event';

/*
 * Shared text style components for updating the text style
 *
 */

type ToggleProps = {
  isActive: boolean;
  onChange: () => void;
  isDisabled?: boolean;
};

type ToggleWithFontFamily = ToggleProps & {
  // Font family is used for checking if the option is disabled (e.g. if the font family doesn't support bold)
  fontFamily: FontFamily | string;
};

export const Bold = memo(({ isActive, onChange, fontFamily, isDisabled: isDisabledProp }: ToggleWithFontFamily) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'labelTextToolbar', useSuspense: false });
  const label = t('fontStyle.bold');

  // the disabled prop will override the font family check
  const hasDisabledProp = typeof isDisabledProp === 'boolean';
  let isDisabled = isDisabledProp ?? true;
  if (fontFamily && !hasDisabledProp) {
    const fontConfig = FONT_FAMILY_CONFIG[fontFamily as FontFamily];
    if (fontConfig?.hasBold) isDisabled = false;
  }

  return (
    <Tooltip hasArrow placement='bottom' label={label} bg='black'>
      <IconButton
        isDisabled={isDisabledProp || isDisabled}
        isActive={isActive && !isDisabled}
        onClick={onChange}
        onMouseDown={preventDefault}
        size='sm'
        aria-label={label}
        variant='icon-btn-toolbar'
        icon={<BoldIcon />}
      />
    </Tooltip>
  );
});
Bold.displayName = 'Bold';

export const Italic = memo(({ isActive, onChange, fontFamily, isDisabled: isDisabledProp }: ToggleWithFontFamily) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'labelTextToolbar', useSuspense: false });
  const label = t('fontStyle.italic');

  // the disabled prop will override the font family check
  const hasDisabledProp = typeof isDisabledProp === 'boolean';
  let isDisabled = isDisabledProp ?? true;
  if (fontFamily && !hasDisabledProp) {
    const fontConfig = FONT_FAMILY_CONFIG[fontFamily as FontFamily];
    if (fontConfig?.hasItalic) isDisabled = false;
  }

  return (
    <Tooltip hasArrow placement='bottom' label={label} bg='black'>
      <IconButton
        isDisabled={isDisabled}
        isActive={isActive && !isDisabled}
        onClick={onChange}
        onMouseDown={preventDefault}
        size='sm'
        aria-label={label}
        variant='icon-btn-toolbar'
        icon={<ItalicIcon />}
      />
    </Tooltip>
  );
});
Italic.displayName = 'Italic';

export const Underline = memo(({ onChange, isActive }: ToggleProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'labelTextToolbar', useSuspense: false });
  const label = t('fontStyle.underline');

  return (
    <Tooltip hasArrow placement='bottom' label={label} bg='black'>
      <IconButton
        isActive={isActive}
        onClick={onChange}
        onMouseDown={preventDefault}
        size='sm'
        aria-label={label}
        variant='icon-btn-toolbar'
        icon={<UnderlineIcon />}
      />
    </Tooltip>
  );
});
Underline.displayName = 'Underline';
